import _ from 'lodash'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import i18n from 'scoreboard/components/i18n'
import fieldAggregationDescription from 'scoreboard/datasets/field-aggregation-description'

/**
 * Returns an appropriate label for a chart breakdown
 *
 * @param {Integer} [datasetId] if breakdown is on a dataset field, the ID of
 * the dataset
 *
 * @param {Integer} [datasetFieldId] if breakdown is on a dataset field, the ID
 * of the field
 *
 * @param {Integer} [datasetVisibleLinkedFieldId] if breakdown is on a dataset
 * field, the ID of the "visible linked field" (if applicable)
 *
 * @param {ChartBreakdownBy} breakdownBy
 *
 * @param {ChartData} domainData - metadata for domain objects used by the chart
 * (datasets, scorecard items, etc)
 *
 * @param {String} useGenericDataType when populated, the label will be generated
 * with generic terms using the supplied data type, rather than finding the field
 * name from a dataset
 *
 * @return {String}
 */
function breakdownLabel ({ datasetId, datasetFieldId, datasetVisibleLinkedFieldId,
	breakdownBy, domainData, useGenericDataType = null }) {
	// #41247 if we are repeating by multiple fields, `datasetId` will be null,
	// but we may not be breaking down by calendar fields
	if (!datasetId && !useGenericDataType) {
		// If there's no dataset, the only possible breakdown type is
		// 'calendar-period-ranges', so just label it "Calendar Period".
		//
		// Note that 'calendar-period-ranges' is also valid when there is a
		// dataset, but in that case, the dataset field name is used instead.
		return i18n.get('common.calendar.calendarPeriod')
	}

	let dataset
	let aggregationField
	let fieldDescription
	if (useGenericDataType) {
		fieldDescription = i18n.get('charts.breakdown.by.generic')
	} else {
		dataset = domainData.datasetModel(datasetId)
		aggregationField = dataset.combinedFieldsById[datasetFieldId]
		let aggregationVisibleLinkedField = dataset.visibleLinkedFieldsById[
			datasetVisibleLinkedFieldId]

		fieldDescription = fieldAggregationDescription({
			dataset,
			aggregationField,
			aggregationVisibleLinkedField,
			aggregationType: 'none',
			tracked: dataset.get('tracked')
		})
	}

	let key = breakdownLabelKey({
		breakdownBy,
		aggregationField,
		dataset,
		dataType: useGenericDataType
	})

	return i18n.get(key, fieldDescription)
}

function breakdownLabelKey ({ breakdownBy, aggregationField, dataset, dataType = null }) {
	if (['calendar-period-ranges', 'calendar-period-grouped-ranges'].includes(breakdownBy)) {
		return 'common.identity'
	} else {
		let key = 'datasets.choose.rows.'

		if (_.includes([ 'specific-values', 'specific-ranges' ], breakdownBy)) {
			key += 'specific.'
		}

		if (_.includes([ 'specific-values', 'every-value' ], breakdownBy)) {
			key += 'values'
			if (dataType) {
				// #41247 if a dataType is provided, then we don't have a field name
				// to show, so we need to display generic text
				key += '.default'
			}
		} else if (_.includes([ 'specific-ranges', 'every-range' ], breakdownBy)) {
			key += 'range.'

			let type = dataType
			if (!dataType) {
				let underlyingField = dataset.getUnderlyingField(aggregationField)
				type = underlyingField.dataType
			}
			key += isNumericDataType(type) ? 'numeric' : 'text'
		}

		return key
	}
}

export default breakdownLabel

breakdownLabel.__filename__ = 'charts/breakdown-label.js'
